import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { LogOut, Download, X, Upload } from 'lucide-react';
import jsPDF from 'jspdf';
import mammoth from 'mammoth';
import founderPortrait from '@/assets/founder-portrait.jpg';

interface BulletinImage {
  dataUrl: string;
  width: number;
  height: number;
  format: 'PNG' | 'JPEG';
}

const readImage = (file: File): Promise<BulletinImage> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Not an image'));
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();
      img.onload = () => {
        const format: 'PNG' | 'JPEG' = file.type === 'image/png' ? 'PNG' : 'JPEG';
        resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight, format });
      };
      img.onerror = () => reject(new Error('Could not load image'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

const urlToImage = (url: string): Promise<BulletinImage> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('canvas'));
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight, format: 'JPEG' });
    };
    img.onerror = () => reject(new Error('img load'));
    img.src = url;
  });

const AdminBulletin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [authChecking, setAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const today = new Date();
  const defaultMonthYear = today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }).toUpperCase();

  const [category, setCategory] = useState('WEEKLY BULLETIN');
  const [monthYear, setMonthYear] = useState(defaultMonthYear);
  const [title, setTitle] = useState('');
  const [coverImage, setCoverImage] = useState<BulletinImage | null>(null);
  const [bodyText, setBodyText] = useState('');
  // Rich blocks captured from a .docx upload (preserves bold/italic/underline + alignment).
  // Cleared whenever the user edits the textarea manually.
  type Align = 'left' | 'center' | 'right' | 'justify';
  type Run = { text: string; bold?: boolean; italic?: boolean; underline?: boolean };
  type RichBlock =
    | { kind: 'heading'; runs: Run[]; align?: Align }
    | { kind: 'paragraph'; runs: Run[]; align?: Align }
    | { kind: 'list'; items: Run[][]; align?: Align };
  const [richBlocks, setRichBlocks] = useState<RichBlock[] | null>(null);
  const [authorName, setAuthorName] = useState('Omollo Victor');
  const [authorTitle, setAuthorTitle] = useState('LLB Hons | CPM | Trainee Advocate');
  const [authorImage, setAuthorImage] = useState<BulletinImage | null>(null);
  
  const [disclaimer, setDisclaimer] = useState(
    'This alert is for informational purposes only and should not be considered or interpreted as legal advice. If you have any questions or require clarification, please feel free to contact the author Omollo Victor, (omollovictor81@gmail.com) – or your usual contact at our firm for legal guidance.'
  );

  useEffect(() => {
    urlToImage(founderPortrait).then(setAuthorImage).catch(() => {});
  }, []);

  useEffect(() => {
    const check = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate('/admin/login', { replace: true });
        return;
      }
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', sessionData.session.user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (!roleData) {
        toast({ title: 'Access denied', description: 'Your account is not an admin.', variant: 'destructive' });
        await supabase.auth.signOut();
        navigate('/admin/login', { replace: true });
        return;
      }
      setIsAdmin(true);
      setAuthChecking(false);
    };
    check();
  }, [navigate, toast]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>, setter: (i: BulletinImage) => void) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Max 8MB', variant: 'destructive' });
      return;
    }
    try {
      setter(await readImage(f));
    } catch (err: any) {
      toast({ title: 'Image error', description: err.message, variant: 'destructive' });
    }
    e.target.value = '';
  };

  const onDocx = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const arrayBuffer = await f.arrayBuffer();

      // Surface paragraph alignment by injecting a sentinel-marked text run at the
      // start of each non-default-aligned paragraph. We strip the marker in our walker
      // and assign it to the block's `align` field.
      const ALIGN_OPEN = '\u0002';
      const ALIGN_CLOSE = '\u0003';
      const alignTransform = (mammoth as any).transforms.paragraph((p: any) => {
        if (p.alignment && p.alignment !== 'left') {
          return {
            ...p,
            children: [{ type: 'text', value: `${ALIGN_OPEN}${p.alignment}${ALIGN_CLOSE}` }, ...p.children],
          };
        }
        return p;
      });

      const { value: html } = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "p[style-name='Title'] => h1:fresh",
            "p[style-name='Subtitle'] => h2:fresh",
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
            "p[style-name='Heading 4'] => h4:fresh",
            "p[style-name='Heading 5'] => h5:fresh",
            "p[style-name='heading 1'] => h1:fresh",
            "p[style-name='heading 2'] => h2:fresh",
            "p[style-name='heading 3'] => h3:fresh",
            "p[style-name='Heading1'] => h1:fresh",
            "p[style-name='Heading2'] => h2:fresh",
            "p[style-name='Heading3'] => h3:fresh",
            "r[style-name='Strong'] => strong",
            "r[style-name='Emphasis'] => em",
          ],
          transformDocument: alignTransform,
        } as any
      );

      const parser = new DOMParser();
      const dom = parser.parseFromString(html, 'text/html');

      const norm = (s: string) => s.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ');

      const collectRuns = (node: Node, state: { b?: boolean; i?: boolean; u?: boolean }, out: Run[]) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = norm(node.textContent || '');
          if (text) out.push({ text, bold: state.b, italic: state.i, underline: state.u });
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        const next = { ...state };
        if (tag === 'strong' || tag === 'b') next.b = true;
        if (tag === 'em' || tag === 'i') next.i = true;
        if (tag === 'u' || tag === 'ins') next.u = true;
        for (const child of Array.from(el.childNodes)) collectRuns(child, next, out);
      };

      // Extract alignment marker from runs and return {align, cleanedRuns}.
      const extractAlign = (runs: Run[]): { align: Align; runs: Run[] } => {
        if (!runs.length) return { align: 'left', runs };
        const m = runs[0].text.match(/^\u0002(left|center|right|both|justify)\u0003/);
        if (!m) return { align: 'left', runs };
        const raw = m[1];
        const align: Align = raw === 'both' ? 'justify' : (raw as Align);
        const first = { ...runs[0], text: runs[0].text.slice(m[0].length).replace(/^\s+/, '') };
        const cleaned = first.text ? [first, ...runs.slice(1)] : runs.slice(1);
        return { align, runs: cleaned };
      };

      const runsFromEl = (el: Element): Run[] => {
        const out: Run[] = [];
        for (const child of Array.from(el.childNodes)) collectRuns(child, {}, out);
        while (out.length && !out[0].text.trim()) out.shift();
        while (out.length && !out[out.length - 1].text.trim()) out.pop();
        if (out.length) {
          out[0].text = out[0].text.replace(/^\s+/, '');
          out[out.length - 1].text = out[out.length - 1].text.replace(/\s+$/, '');
        }
        return out.filter((r) => r.text.length > 0);
      };

      const rich: RichBlock[] = [];

      // Heuristic: if a <p> looks like a heading (all bold, or short title-like
      // line with no terminal punctuation), promote it. Word docs frequently
      // style headings via direct formatting instead of paragraph styles, which
      // is why mammoth emits them as <p>.
      const looksLikeHeading = (runs: Run[]): boolean => {
        if (!runs.length) return false;
        const text = runs.map((r) => r.text).join('').trim();
        if (!text) return false;
        if (text.length > 120) return false;
        const allBold = runs.every((r) => r.bold || !r.text.trim());
        const hasTerminal = /[.!?:;,"\u201D\u2019]\s*$/.test(text);
        if (allBold && !hasTerminal) return true;
        if (text.length <= 90 && !hasTerminal && /^[A-Z0-9"\u201C(]/.test(text)) return true;
        return false;
      };

      const walk = (parent: Element) => {
        for (const child of Array.from(parent.children)) {
          const tag = child.tagName.toLowerCase();
          if (/^h[1-6]$/.test(tag)) {
            const { align, runs } = extractAlign(runsFromEl(child));
            if (runs.length) rich.push({ kind: 'heading', runs, align });
          } else if (tag === 'p') {
            const { align, runs } = extractAlign(runsFromEl(child));
            if (!runs.length) continue;
            if (looksLikeHeading(runs)) {
              // Strip bold from runs since heading style already bolds.
              const clean = runs.map((r) => ({ ...r, bold: false }));
              rich.push({ kind: 'heading', runs: clean, align });
            } else {
              rich.push({ kind: 'paragraph', runs, align });
            }
          } else if (tag === 'ul' || tag === 'ol') {
            const items = Array.from(child.querySelectorAll(':scope > li'))
              .map((li) => extractAlign(runsFromEl(li)).runs)
              .filter((r) => r.length > 0);
            if (items.length) rich.push({ kind: 'list', items });
          } else if (tag === 'blockquote') {
            const { align, runs } = extractAlign(runsFromEl(child));
            if (runs.length) rich.push({ kind: 'paragraph', runs, align });
          } else if (child.children.length) {
            walk(child);
          } else {
            const { align, runs } = extractAlign(runsFromEl(child));
            if (runs.length) rich.push({ kind: 'paragraph', runs, align });
          }
        }
      };
      walk(dom.body);

      // Mirror to plain text in the textarea so the user can preview/edit copy.
      const HEADING = '\u0001';
      const plain = rich
        .map((b) => {
          if (b.kind === 'heading') return HEADING + b.runs.map((r) => r.text).join('');
          if (b.kind === 'list') return b.items.map((it) => '• ' + it.map((r) => r.text).join('')).join('\n');
          return b.runs.map((r) => r.text).join('');
        })
        .join('\n\n');

      setRichBlocks(rich);
      setBodyText(plain);
      toast({ title: 'Document loaded', description: `Imported ${rich.length} blocks from ${f.name} (formatting preserved)` });
    } catch (err: any) {
      toast({ title: 'Could not read docx', description: err.message, variant: 'destructive' });
    }
    e.target.value = '';
  };

  const [generatingArt, setGeneratingArt] = useState(false);

  const fetchThemedBg = async (topic: string, variant: 'cover' | 'back'): Promise<BulletinImage | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-bulletin-bg', {
        body: { topic, variant },
      });
      if (error) throw error;
      const url = (data as any)?.imageUrl as string | undefined;
      if (!url) return null;
      return await urlToImage(url);
    } catch (err) {
      console.warn('Themed background generation failed', err);
      return null;
    }
  };

  const handleDownload = async () => {
    if (!title.trim()) {
      toast({ title: 'Missing title', description: 'Add a bulletin title', variant: 'destructive' });
      return;
    }
    if (!bodyText.trim()) {
      toast({ title: 'Missing content', description: 'Paste text or upload a .docx', variant: 'destructive' });
      return;
    }

    setGeneratingArt(true);
    toast({ title: 'Generating themed artwork…', description: 'Creating bespoke cover & back illustrations.' });
    const topic = `${category} — ${title}`;
    const [frontBg, backBg] = await Promise.all([
      fetchThemedBg(topic, 'cover'),
      fetchThemedBg(topic, 'back'),
    ]);
    setGeneratingArt(false);
    if (!frontBg || !backBg) {
      toast({
        title: 'Artwork fallback',
        description: 'Could not generate themed artwork — using a navy fallback for this run.',
        variant: 'destructive',
      });
    }

    const NAVY: [number, number, number] = [0, 0, 128];
    const GOLD: [number, number, number] = [196, 154, 73];
    const TEXT: [number, number, number] = [25, 25, 35];
    const MUTED: [number, number, number] = [100, 105, 115];
    const WHITE: [number, number, number] = [255, 255, 255];

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const marginX = 50;
    const contentW = pageW - marginX * 2;
    const colGap = 24;
    const colW = (contentW - colGap) / 2;
    const colXLeft = marginX;
    const colXRight = marginX + colW + colGap;

    // ============== COVER ==============
    // Full-bleed themed artwork (AI-generated) — never a solid colour.
    const drawCoverFill = (bg: BulletinImage | null) => {
      if (bg) {
        // Cover-fit: scale to fill page, crop overflow.
        const pageRatio = pageW / pageH;
        const imgRatio = bg.width / bg.height;
        let drawW = pageW, drawH = pageH, dx = 0, dy = 0;
        if (imgRatio > pageRatio) {
          drawH = pageH;
          drawW = drawH * imgRatio;
          dx = (pageW - drawW) / 2;
        } else {
          drawW = pageW;
          drawH = drawW / imgRatio;
          dy = (pageH - drawH) / 2;
        }
        doc.addImage(bg.dataUrl, bg.format, dx, dy, drawW, drawH, undefined, 'FAST');
      } else {
        doc.setFillColor(...NAVY);
        doc.rect(0, 0, pageW, pageH, 'F');
      }
    };

    const withOpacity = (op: number, fn: () => void) => {
      // jsPDF GState transparency.
      const GS = (doc as any).GState;
      if (GS) {
        const gs = new GS({ opacity: op });
        (doc as any).setGState(gs);
        fn();
        (doc as any).setGState(new GS({ opacity: 1 }));
      } else {
        fn();
      }
    };

    drawCoverFill(frontBg);

    // Translucent navy bands for legibility (top masthead + bottom title).
    withOpacity(0.62, () => {
      doc.setFillColor(...NAVY);
      doc.rect(0, 0, pageW, 145, 'F');
    });
    withOpacity(0.7, () => {
      doc.setFillColor(...NAVY);
      doc.rect(0, pageH - 230, pageW, 230, 'F');
    });

    doc.setDrawColor(...WHITE);
    doc.setLineWidth(1.2);
    doc.rect(28, 28, pageW - 56, pageH - 56);

    doc.setTextColor(...WHITE);
    doc.setFont('times', 'normal');
    doc.setFontSize(11);
    doc.text(monthYear, pageW - 48, 70, { align: 'right' });

    doc.setFont('times', 'bold');
    doc.setFontSize(13);
    doc.setCharSpace(2);
    doc.text('LAW & CODE AFRICA', pageW / 2, 95, { align: 'center' });
    doc.setCharSpace(0);
    doc.setFont('times', 'italic');
    doc.setFontSize(8.5);
    doc.text('Commentary on Law, Technology & Society', pageW / 2, 110, { align: 'center' });

    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.8);
    doc.line(pageW / 2 - 60, 118, pageW / 2 + 60, 118);

    // Category banner (translucent gold strip) sits on the bottom band.
    withOpacity(0.85, () => {
      doc.setFillColor(...GOLD);
      doc.rect(0, pageH - 230, pageW, 4, 'F');
    });

    // Auto-fit the category headline so it always sits inside the cover safe area.
    {
      const catText = category.toUpperCase();
      const maxWidth = pageW - 100;
      const charSp = 4;
      doc.setFont('times', 'bold');
      let catSize = 40;
      doc.setCharSpace(charSp);
      while (catSize > 14) {
        doc.setFontSize(catSize);
        if (doc.getTextWidth(catText) <= maxWidth) break;
        catSize -= 1;
      }
      doc.setFontSize(catSize);
      doc.setTextColor(...WHITE);
      doc.text(catText, pageW / 2, pageH - 175, { align: 'center' });
      doc.setCharSpace(0);
    }


    doc.setFont('times', 'italic');
    doc.setFontSize(18);
    doc.setTextColor(...WHITE);
    const titleLines = doc.splitTextToSize(title, pageW - 120) as string[];
    let titleY = pageH - 130;
    titleLines.forEach((ln) => {
      doc.text(ln, pageW / 2, titleY, { align: 'center' });
      titleY += 22;
    });

    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.6);
    doc.line(pageW / 2 - 40, pageH - 70, pageW / 2 + 40, pageH - 70);


    // ============== INNER PAGES ==============
    let page = 1;
    const bannerH = 200;

    const drawInnerHeader = () => {
      if (coverImage) {
        const ratio = coverImage.width / coverImage.height;
        const drawW = pageW;
        const drawH = drawW / ratio;
        doc.addImage(coverImage.dataUrl, coverImage.format, 0, bannerH - drawH, drawW, drawH, undefined, 'FAST');
        doc.setFillColor(...WHITE);
        doc.rect(0, bannerH, pageW, pageH - bannerH, 'F');
      } else {
        doc.setFillColor(...NAVY);
        doc.rect(0, 0, pageW, bannerH, 'F');
      }
      doc.setFillColor(...GOLD);
      doc.rect(0, bannerH, pageW, 3, 'F');

      doc.setFont('times', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(...WHITE);
      const tLines = doc.splitTextToSize(title, pageW - 100) as string[];
      let ty = 60;
      const bandH = tLines.length * 26 + 24;
      doc.setGState(new (doc as any).GState({ opacity: 0.55 }));
      doc.setFillColor(...NAVY);
      doc.rect(0, 30, pageW, bandH, 'F');
      doc.setGState(new (doc as any).GState({ opacity: 1 }));
      doc.setTextColor(...WHITE);
      tLines.forEach((ln) => {
        doc.text(ln, marginX, ty);
        ty += 26;
      });
    };

    const drawFooter = (pageLabel: string) => {
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.6);
      doc.line(marginX, pageH - 46, pageW - marginX, pageH - 46);
      doc.setFont('times', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...MUTED);
      const catLabel = category.toLowerCase().replace(/(^|\s)\w/g, (m) => m.toUpperCase());
      doc.text(`${catLabel} | Law & Code Africa`, marginX, pageH - 30);
      doc.text(pageLabel, pageW - marginX, pageH - 30, { align: 'right' });
    };

    let col: 0 | 1 = 0;
    const yTop = bannerH + 26;
    const yBottom = pageH - 60;
    let cy = yTop;

    const drawColumnDivider = () => {
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.6);
      const dividerX = marginX + colW + colGap / 2;
      doc.line(dividerX, yTop, dividerX, yBottom);
    };

    const startNewInnerPage = () => {
      drawFooter(`Page 0${page}`);
      doc.addPage();
      page += 1;
      drawInnerHeader();
      col = 0;
      cy = yTop;
      drawColumnDivider();
    };

    const moveToNextColumnOrPage = () => {
      if (col === 0) {
        col = 1;
        cy = yTop;
      } else {
        startNewInnerPage();
      }
    };

    const colX = () => (col === 0 ? colXLeft : colXRight);

    const writeColumnHeading = (text: string) => {
      doc.setFont('times', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(...NAVY);
      const lines = doc.splitTextToSize(text, colW) as string[];
      const lineH = 16;
      const block = lines.length * lineH + 8;
      if (cy + block > yBottom) moveToNextColumnOrPage();
      lines.forEach((ln) => {
        doc.text(ln, colX(), cy);
        cy += lineH;
      });
      cy += 6;
    };

    const writeColumnParagraph = (text: string, opts: { italic?: boolean; size?: number; color?: [number, number, number] } = {}) => {
      const { italic = false, size = 10.5, color = TEXT } = opts;
      doc.setFont('times', italic ? 'italic' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, colW) as string[];
      const lineH = size * 1.4;
      for (const ln of lines) {
        if (cy + lineH > yBottom) moveToNextColumnOrPage();
        doc.text(ln, colX(), cy);
        cy += lineH;
      }
      cy += 6;
    };

    // --- Rich (formatted) writers: word-wrap runs with bold/italic/underline ---
    const fontStyleFor = (r: Run) =>
      r.bold && r.italic ? 'bolditalic' : r.bold ? 'bold' : r.italic ? 'italic' : 'normal';

    type Tok = { text: string; run: Run; isSpace: boolean };
    const tokenize = (runs: Run[]): Tok[] => {
      const toks: Tok[] = [];
      for (const r of runs) {
        const parts = r.text.split(/(\s+)/);
        for (const p of parts) {
          if (!p) continue;
          toks.push({ text: p, run: r, isSpace: /^\s+$/.test(p) });
        }
      }
      return toks;
    };

    const measure = (text: string, run: Run, size: number) => {
      doc.setFont('times', fontStyleFor(run));
      doc.setFontSize(size);
      return doc.getTextWidth(text);
    };

    const writeRichRuns = (
      runs: Run[],
      opts: {
        size?: number;
        color?: [number, number, number];
        bold?: boolean;
        indent?: number;
        trailingGap?: number;
        align?: Align;
      } = {}
    ) => {
      const { size = 10.5, color = TEXT, bold = false, indent = 0, trailingGap = 6, align = 'justify' } = opts;
      const lineH = size * 1.4;
      const maxW = colW - indent;
      const toks = tokenize(runs);

      let line: Tok[] = [];
      let lineW = 0;
      let lastLine = false;

      const flush = () => {
        if (!line.length) return;
        if (cy + lineH > yBottom) moveToNextColumnOrPage();

        // Measure non-space and space widths separately for alignment math.
        let nonSpaceW = 0;
        let spaceCount = 0;
        for (const t of line) {
          const w = measure(t.text, bold ? { ...t.run, bold: true } : t.run, size);
          if (t.isSpace) spaceCount += 1;
          else nonSpaceW += w;
        }
        const naturalSpaceW = line
          .filter((t) => t.isSpace)
          .reduce((s, t) => s + measure(t.text, bold ? { ...t.run, bold: true } : t.run, size), 0);
        const naturalLineW = nonSpaceW + naturalSpaceW;

        let startX = colX() + indent;
        let spaceExtra = 0;
        const remaining = maxW - naturalLineW;
        if (align === 'right') startX += Math.max(0, remaining);
        else if (align === 'center') startX += Math.max(0, remaining / 2);
        else if (align === 'justify' && !lastLine && spaceCount > 0 && remaining > 0 && remaining < maxW * 0.3) {
          spaceExtra = remaining / spaceCount;
        }

        let x = startX;
        for (const t of line) {
          const effRun: Run = bold ? { ...t.run, bold: true } : t.run;
          doc.setFont('times', fontStyleFor(effRun));
          doc.setFontSize(size);
          doc.setTextColor(...color);
          const w = doc.getTextWidth(t.text);
          if (t.isSpace) {
            x += w + spaceExtra;
          } else {
            doc.text(t.text, x, cy);
            if (effRun.underline) {
              const uy = cy + 1.5;
              doc.setDrawColor(...color);
              doc.setLineWidth(0.5);
              doc.line(x, uy, x + w, uy);
            }
            x += w;
          }
        }
        cy += lineH;
        line = [];
        lineW = 0;
      };

      let i = 0;
      while (i < toks.length) {
        const t = toks[i];
        if (line.length === 0 && t.isSpace) { i++; continue; }
        const w = measure(t.text, bold ? { ...t.run, bold: true } : t.run, size);
        if (lineW + w > maxW && line.length > 0) {
          while (line.length && line[line.length - 1].isSpace) {
            lineW -= measure(line[line.length - 1].text, line[line.length - 1].run, size);
            line.pop();
          }
          flush();
          continue;
        }
        line.push(t);
        lineW += w;
        i++;
      }
      while (line.length && line[line.length - 1].isSpace) line.pop();
      lastLine = true;
      flush();
      cy += trailingGap;
    };

    const writeRichHeading = (runs: Run[], align: Align = 'left') => {
      const size = 13;
      const lineH = size * 1.25;
      // Only break to next column if there's no room for even one line of the heading.
      if (cy + lineH > yBottom) moveToNextColumnOrPage();
      writeRichRuns(runs, { size, color: NAVY, bold: true, trailingGap: 8, align });
    };

    doc.addPage();
    drawInnerHeader();
    drawColumnDivider();

    if (richBlocks && richBlocks.length) {
      for (const b of richBlocks) {
        if (b.kind === 'heading') {
          writeRichHeading(b.runs, b.align || 'left');
        } else if (b.kind === 'paragraph') {
          writeRichRuns(b.runs, { align: b.align || 'justify' });
        } else if (b.kind === 'list') {
          for (const item of b.items) {
            writeRichRuns([{ text: '•  ' }, ...item], { indent: 0, trailingGap: 2, align: 'left' });
          }
          cy += 4;
        }
      }
    } else {
      // Fallback: parse plain bodyText (paste path).
      const HEADING_MARK = '\u0001';
      const blocks = bodyText.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
      blocks.forEach((rawBlock) => {
        let block = rawBlock;
        let isHeading = false;
        if (block.startsWith(HEADING_MARK)) {
          isHeading = true;
          block = block.slice(1).trim();
        } else {
          isHeading = block.length < 80 && !/[.!?:;]$/.test(block) && !block.includes('\n');
        }
        if (isHeading) writeColumnHeading(block);
        else writeColumnParagraph(block.replace(/\n+/g, ' '));
      });
    }

    // Author block: place inline right after the body (no forced column jump
    // — that was leaving entire half-pages blank). Only break to a new column
    // or page if there isn't enough room for the full block + disclaimer.
    {
      const needed = 200 + (disclaimer.trim() ? 90 : 0);
      if (cy + needed > yBottom) moveToNextColumnOrPage();
      cy += 12; // small breathing space above author block
    }

    if (authorImage) {
      const aw = 120;
      const ah = 145;
      const ax = colX() + (colW - aw) / 2;
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(1.2);
      doc.rect(ax - 3, cy - 3, aw + 6, ah + 6);
      doc.addImage(authorImage.dataUrl, authorImage.format, ax, cy, aw, ah, undefined, 'FAST');
      cy += ah + 16;
    }
    doc.setFont('times', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...NAVY);
    doc.text(authorName, colX() + colW / 2, cy, { align: 'center' });
    cy += 8;
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.6);
    doc.line(colX() + 30, cy, colX() + colW - 30, cy);
    cy += 14;
    doc.setFont('times', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    const atLines = doc.splitTextToSize(authorTitle, colW) as string[];
    atLines.forEach((ln) => {
      doc.text(ln, colX() + colW / 2, cy, { align: 'center' });
      cy += 13;
    });

    // Disclaimer block — bottom of the right column on the final inner page.
    if (disclaimer.trim()) {
      const discText = disclaimer.trim();
      doc.setFont('times', 'italic');
      doc.setFontSize(8.5);
      const discLines = doc.splitTextToSize(discText, colW) as string[];
      const discBlockH = 16 + discLines.length * 11 + 10;
      // Anchor to bottom of column if there's room, otherwise continue from cy.
      const anchorY = Math.max(cy + 18, yBottom - discBlockH);
      let dy = anchorY;
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.5);
      doc.line(colX() + 20, dy, colX() + colW - 20, dy);
      dy += 12;
      doc.setFont('times', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...NAVY);
      doc.text('DISCLAIMER', colX() + colW / 2, dy, { align: 'center' });
      dy += 11;
      doc.setFont('times', 'italic');
      doc.setTextColor(...MUTED);
      discLines.forEach((ln) => {
        doc.text(ln, colX(), dy);
        dy += 11;
      });
    }

    drawFooter(`Page 0${page}`);

    // ============== BACK COVER ==============
    doc.addPage();
    drawCoverFill(backBg);

    // Translucent navy bands for masthead (top) and contact block (centre/bottom).
    withOpacity(0.62, () => {
      doc.setFillColor(...NAVY);
      doc.rect(0, 0, pageW, 180, 'F');
    });
    withOpacity(0.7, () => {
      doc.setFillColor(...NAVY);
      doc.rect(0, pageH - 260, pageW, 260, 'F');
    });

    doc.setDrawColor(...WHITE);
    doc.setLineWidth(1.2);
    doc.rect(28, 28, pageW - 56, pageH - 56);

    doc.setFont('times', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...WHITE);
    doc.setCharSpace(3);
    doc.text('LAW & CODE AFRICA', pageW / 2, 130, { align: 'center' });
    doc.setCharSpace(0);
    doc.setFont('times', 'italic');
    doc.setFontSize(10);
    doc.text('Commentary on Law, Technology & Society', pageW / 2, 152, { align: 'center' });
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.8);
    doc.line(pageW / 2 - 70, 162, pageW / 2 + 70, 162);


    doc.setFont('times', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...WHITE);
    const contactY = pageH - 200;
    const lines = ['Nairobi, Kenya', 'E: contact@lawandcodeafrica.com', 'W: www.lawandcodeafrica.com'];
    lines.forEach((ln, i) => doc.text(ln, pageW / 2, contactY + i * 18, { align: 'center' }));

    doc.setFont('times', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...GOLD);
    doc.text(`${category} • ${monthYear}`, pageW / 2, pageH - 80, { align: 'center' });

    const safe = monthYear.replace(/[^a-z0-9-]+/gi, '-').toLowerCase();
    doc.save(`law-and-code-africa-bulletin-${safe || 'draft'}.pdf`);
  };

  if (authChecking) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Checking access...</div>;
  }
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-white">
      <section className="py-12 bg-primary text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Weekly Bulletin Drafting</h1>
            <p className="text-blue-100">Paste your bulletin text or upload a .docx — we'll lay it out.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 md:px-8 space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Cover</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category (cover headline)</Label>
                <Input id="category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="WEEKLY BULLETIN" />
              </div>
              <div>
                <Label htmlFor="monthYear">Month & Year</Label>
                <Input id="monthYear" value={monthYear} onChange={(e) => setMonthYear(e.target.value)} placeholder="APRIL 2026" />
              </div>
            </div>
            <div>
              <Label htmlFor="title">Bulletin Title (article subject)</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. AI Regulation in Kenya: What Lawyers Need to Know" />
            </div>
            <div>
              <Label htmlFor="cover">Cover Image</Label>
              <Input id="cover" type="file" accept="image/*" onChange={(e) => onFile(e, setCoverImage)} />
              {coverImage && (
                <div className="relative mt-2 w-full max-w-md border border-gray-200 rounded overflow-hidden">
                  <img src={coverImage.dataUrl} alt="Cover" className="w-full h-auto" />
                  <button type="button" onClick={() => setCoverImage(null)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1" aria-label="Remove cover">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-xl font-semibold">Bulletin Content</h2>
              <div>
                <input id="docxUpload" type="file" accept=".docx" onChange={onDocx} className="hidden" />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('docxUpload')?.click()}>
                  <Upload className="w-4 h-4 mr-2" /> Upload .docx
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Paste the full bulletin text below, or upload a Word document. Separate paragraphs with a blank line. Short standalone lines (under 80 chars, no ending punctuation) are treated as section headings.
            </p>
            <Textarea
              value={bodyText}
              onChange={(e) => { setBodyText(e.target.value); setRichBlocks(null); }}
              rows={20}
              placeholder="Paste your bulletin content here, or click 'Upload .docx' above to load from a Word document..."
              className="font-serif"
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Author (last article page)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="authorName">Author Name</Label>
                <Input id="authorName" value={authorName} onChange={(e) => setAuthorName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="authorTitle">Author Title</Label>
                <Input id="authorTitle" value={authorTitle} onChange={(e) => setAuthorTitle(e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="authorPhoto">Author Photo (optional — defaults to founder portrait)</Label>
              <Input id="authorPhoto" type="file" accept="image/*" onChange={(e) => onFile(e, setAuthorImage)} />
              {authorImage && (
                <div className="relative mt-2 w-32 h-40 border border-gray-200 rounded overflow-hidden">
                  <img src={authorImage.dataUrl} alt="Author" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="disclaimer">Disclaimer (printed below the author block)</Label>
              <Textarea
                id="disclaimer"
                value={disclaimer}
                onChange={(e) => setDisclaimer(e.target.value)}
                rows={4}
                className="font-serif"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleDownload} size="lg" disabled={generatingArt}>
              <Download className="w-4 h-4 mr-2" /> {generatingArt ? 'Generating themed artwork…' : 'Download Bulletin PDF'}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminBulletin;
